/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Play, Pause, SkipForward, SkipBack, Volume2, VolumeX, ListMusic } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import { audioTracks } from "../mockData";
import { useAudio } from "../context/AudioContext";

const AudioPlayer = ({ showMenu = false }) => {
  const location = useLocation();
  const tracks = useMemo(() => audioTracks, []);

  // Mobile uniquement : sur un article Actualités, le lecteur se place
  // en bas de l'écran, sous la zone "Projet précédent / Projet suivant".
  // Il reste monté en permanence : aucune coupure audio lors des navigations.
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  const isBlogPostMobile =
    isMobile && /^\/actualites\/[^/]+\/?$/.test(location.pathname);

  const isPhotosPage = /^\/photos\/?$/.test(location.pathname);

  useEffect(() => {
    const updateMobile = () => setIsMobile(window.innerWidth < 768);
    updateMobile();
    window.addEventListener("resize", updateMobile);
    return () => window.removeEventListener("resize", updateMobile);
  }, []);
  const { setActivePlayer, startMiniExclusivity, isMiniExclusivityActive, miniPlayerAudioRef } = useAudio();

  const [currentTrack, setCurrentTrack] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showVolume, setShowVolume] = useState(false);
  const [isAdjustingVolume, setIsAdjustingVolume] = useState(false);
  const [showPlaylist, setShowPlaylist] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const audioRef = useRef(null);

  // --- POSITION DU PLAYER (inchangé) ---
  const getNavigationOffset = () => {
    const navElement =
      document.querySelector(".navigation") ||
      document.querySelector("nav") ||
      document.querySelector('[class*="nav"]') ||
      document.querySelector("header");

    if (navElement) {
      const navRect = navElement.getBoundingClientRect();
      const isLandscape = window.innerHeight < 500;
      const margin = isLandscape ? 15 : 25;
      return navRect.bottom + margin;
    }
    return window.innerHeight < 500 ? 75 : 90;
  };

  const getInitialPosition = () => {
    const playerWidth = 320;
    const playerHeight = 80;
    const navOffset = getNavigationOffset();
    let x = (window.innerWidth - playerWidth) / 2;
    let y = navOffset + 5;
    x = Math.max(10, Math.min(x, window.innerWidth - playerWidth - 10));
    y = Math.max(0, Math.min(y, window.innerHeight - playerHeight - 10));
    return { x, y };
  };

  const [position, setPosition] = useState(() => {
    try { return getInitialPosition(); } catch { return { x: 50, y: 100 }; }
  });

  useEffect(() => {
    if (!isInitialized) {
      try {
        const initial = getInitialPosition();
        const actualWidth = playerRef.current?.offsetWidth || 320;
        initial.x = Math.max(10, (window.innerWidth - actualWidth) / 2);
        setPosition(initial);
        setIsInitialized(true);
      }
      catch { setPosition({ x: 50, y: 100 }); setIsInitialized(true); }
    }
  }, [isInitialized]);

  useEffect(() => {
    if (!isInitialized || !isPhotosPage || isBlogPostMobile) return;

    const placePhotosPlayer = () => {
      const playerWidth = playerRef.current?.offsetWidth || 320;
      const playerHeight = playerRef.current?.offsetHeight || 80;

      if (isMobile) {
        setPosition({
          x: Math.max(10, (window.innerWidth - playerWidth) / 2),
          y: Math.max(getNavigationOffset(), window.innerHeight - playerHeight - 18),
        });
      } else {
        // Desktop Photos : aligné à droite de la zone centrale du site
        // (max-w-7xl = 1280px), au lieu du bord droit de l'écran.
        const contentMaxWidth = 1280;
        const contentWidth = Math.min(window.innerWidth, contentMaxWidth);
        const contentLeft = Math.max(0, (window.innerWidth - contentWidth) / 2);

        setPosition({
          x: Math.max(10, contentLeft + contentWidth - playerWidth - 40),
          y: Math.max(getNavigationOffset(), getNavigationOffset() + 20),
        });
      }
    };

    const raf = window.requestAnimationFrame(placePhotosPlayer);
    return () => window.cancelAnimationFrame(raf);
  }, [location.pathname, isInitialized, isPhotosPage, isBlogPostMobile, isMobile]);

  // En quittant Photos, le lecteur reprend sa position normale centrée.
  const previousPathRef = useRef(location.pathname);

  useEffect(() => {
    const previousPath = previousPathRef.current;
    const leftPhotos = /^\/photos\/?$/.test(previousPath) && !isPhotosPage;

    if (leftPhotos && !isBlogPostMobile) {
      const raf = window.requestAnimationFrame(() => {
        try {
          const normal = getInitialPosition();
          const actualWidth = playerRef.current?.offsetWidth || 320;
          normal.x = Math.max(10, (window.innerWidth - actualWidth) / 2);
          setPosition(normal);
        } catch {}
      });

      previousPathRef.current = location.pathname;
      return () => window.cancelAnimationFrame(raf);
    }

    previousPathRef.current = location.pathname;
  }, [location.pathname, isPhotosPage, isBlogPostMobile]);

  useEffect(() => {
    let timeout;
    const handleOrientationChange = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        if (isInitialized) {
          try { setPosition(getInitialPosition()); } catch {}
        }
      }, 200);
    };
    window.addEventListener("resize", handleOrientationChange);
    window.addEventListener("orientationchange", handleOrientationChange);
    const mediaQuery = window.matchMedia("(orientation: landscape)");
    mediaQuery.addEventListener("change", handleOrientationChange);
    return () => {
      clearTimeout(timeout);
      window.removeEventListener("resize", handleOrientationChange);
      window.removeEventListener("orientationchange", handleOrientationChange);
      mediaQuery.removeEventListener("change", handleOrientationChange);
    };
  }, [isInitialized]);

  // --- LOGIQUE AUDIO ---
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const safeSrc = tracks[currentTrack]?.url || tracks[currentTrack]?.src;
    if (!safeSrc) return;
    audio.src = encodeURI(safeSrc);
    audio.load();
    setCurrentTime(0);
    setDuration(0);
  }, [currentTrack, tracks]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.play().catch(() => setIsPlaying(false));
    } else {
      audio.pause();
    }
  }, [isPlaying, currentTrack]);

  // --- PAUSE DES VIDÉOS DU BLOG ---
  const pauseBlogVideos = useCallback(() => {
    try {
      // Pause les vidéos HTML5 du blog
      const videos = document.querySelectorAll('video.blog-video-player');
      videos.forEach((v) => {
        if (!v.paused) {
          try { v.pause(); } catch {}
        }
      });

      // Pause les iframes YouTube du blog
      const blogYouTube = document.querySelector('#blog-youtube-player');
      if (blogYouTube) {
        try {
          blogYouTube.contentWindow?.postMessage(
            JSON.stringify({ event: "command", func: "pauseVideo", args: [] }),
            "*"
          );
        } catch {}
      }
    } catch {}
  }, []);

  const pauseYouTubeIframes = useCallback(() => {
    try {
      const iframes = document.querySelectorAll('iframe[src*="youtube.com/embed"]');
      iframes.forEach((f) => {
        try {
          f.contentWindow?.postMessage(
            JSON.stringify({ event: "command", func: "pauseVideo", args: [] }),
            "*"
          );
        } catch {}
      });
    } catch {}
  }, []);

  const pauseSpotifyEmbeds = useCallback(() => {
    try {
      const spotifyControllersRef = useAudio().spotifyControllersRef || { current: new Map() };
      spotifyControllersRef.current.forEach((ctrl) => {
        try { ctrl?.pause?.(); } catch {}
      });
    } catch {}
  }, []);

  // 🔒 Lecture immédiate (même "user gesture") + verrou d'exclusivité "fort"
  const playMiniNow = useCallback(() => {
    const a = audioRef.current;
    if (!a) { setIsPlaying(true); return; }

    // Verrou + pause en boucle des externes
    startMiniExclusivity(1800);

    // Pause TOUTES les sources externes
    pauseYouTubeIframes();
    pauseSpotifyEmbeds();
    pauseBlogVideos(); // ✅ AJOUT : pause des vidéos blog

    // Marque navigation et joue TOUT DE SUITE
    setActivePlayer("navigation");
    const tryPlay = () =>
      a.play()
        .then(() => setIsPlaying(true))
        .catch(() => {
          const onCanPlay = () => {
            a.removeEventListener("canplay", onCanPlay);
            a.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
          };
          a.addEventListener("canplay", onCanPlay, { once: true });
          try { a.load(); } catch {}
        });
    tryPlay();
  }, [startMiniExclusivity, setActivePlayer, pauseYouTubeIframes, pauseSpotifyEmbeds, pauseBlogVideos]);

  // Synchronise états + informe le contexte
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const handlePlay = () => { setIsPlaying(true); setActivePlayer("navigation"); };
    const handlePause = () => setIsPlaying(false);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    return () => {
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
    };
  }, [setActivePlayer]);

  // --- MEDIA SESSION & AUTRES MEDIAS ---
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const checkOtherMediaPlaying = () => {
      // ⚠️ Ne JAMAIS couper le mini si le verrou est actif
      if (isMiniExclusivityActive && isMiniExclusivityActive()) return;

      // Vérifier toutes les vidéos du blog (avec classe spécifique)
      const others = Array.from(
        document.querySelectorAll('audio:not(#mini-player-audio), video.blog-video-player')
      ).some((el) => !el.paused && el.currentTime > 0 && el.readyState >= 2);

      if (others && isPlaying && !audio.paused) {
        setIsPlaying(false);
        audio.pause();
      }
    };

    const intervalId = setInterval(checkOtherMediaPlaying, 250);
    if ("mediaSession" in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: tracks[currentTrack]?.title || "Unknown",
        artist: tracks[currentTrack]?.artist || "Charles Ludig",
        album: "Charles Ludig Portfolio",
      });
      navigator.mediaSession.setActionHandler("play", () => { playMiniNow(); });
      navigator.mediaSession.setActionHandler("pause", () => { setIsPlaying(false); });
      navigator.mediaSession.setActionHandler("previoustrack", () => { handlePrevTrack(); });
      navigator.mediaSession.setActionHandler("nexttrack", () => { handleNextTrack(); });
    }

    return () => clearInterval(intervalId);
  }, [isPlaying, currentTrack, tracks, playMiniNow, isMiniExclusivityActive]);

  // --- CONTROLES ---
  const handlePlayPause = () => {
    const a = audioRef.current;
    if (!a) return;
    if (isPlaying) { setIsPlaying(false); a.pause(); return; }
    playMiniNow(); // ✅ 1 seul clic
  };

  const handleNextTrack = () => {
    const a = audioRef.current;
    setCurrentTrack((i) => (i + 1) % tracks.length);
    startMiniExclusivity(1800);
    setActivePlayer("navigation");
    setTimeout(() => {
      if (!a) return;
      const onLoaded = () => {
        a.removeEventListener("loadeddata", onLoaded);
        a.play().then(() => setIsPlaying(true)).catch(() => {
          const onCanPlay = () => { a.removeEventListener("canplay", onCanPlay); a.play().then(() => setIsPlaying(true)).catch(() => {}); };
          a.addEventListener("canplay", onCanPlay, { once: true });
          try { a.load(); } catch {}
        });
      };
      a.addEventListener("loadeddata", onLoaded, { once: true });
      try { a.load(); } catch {}
    }, 0);
  };

  const handlePrevTrack = () => {
    const a = audioRef.current;
    setCurrentTrack((i) => (i - 1 + tracks.length) % tracks.length);
    startMiniExclusivity(1800);
    setActivePlayer("navigation");
    setTimeout(() => {
      if (!a) return;
      const onLoaded = () => {
        a.removeEventListener("loadeddata", onLoaded);
        a.play().then(() => setIsPlaying(true)).catch(() => {
          const onCanPlay = () => { a.removeEventListener("canplay", onCanPlay); a.play().then(() => setIsPlaying(true)).catch(() => {}); };
          a.addEventListener("canplay", onCanPlay, { once: true });
          try { a.load(); } catch {}
        });
      };
      a.addEventListener("loadeddata", onLoaded, { once: true });
      try { a.load(); } catch {}
    }, 0);
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      const d = audioRef.current.duration;
      setDuration(Number.isFinite(d) ? d : 0);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) setCurrentTime(audioRef.current.currentTime || 0);
  };

  const handleEnded = () => {
    const a = audioRef.current;
    const nextTrack = (currentTrack + 1) % tracks.length;
    setCurrentTrack(nextTrack);
    startMiniExclusivity(1800);
    setActivePlayer("navigation");
    setTimeout(() => {
      if (!a) return;
      const onLoaded = () => {
        a.removeEventListener("loadeddata", onLoaded);
        a.play().then(() => setIsPlaying(true)).catch(() => {
          const onCanPlay = () => { a.removeEventListener("canplay", onCanPlay); a.play().then(() => setIsPlaying(true)).catch(() => {}); };
          a.addEventListener("canplay", onCanPlay, { once: true });
          try { a.load(); } catch {}
        });
      };
      a.addEventListener("loadeddata", onLoaded, { once: true });
      try { a.load(); } catch {}
    }, 0);
  };

  const handleVolumeChange = (e) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    setIsMuted(v === 0);
    if (audioRef.current) {
      audioRef.current.volume = v;
      audioRef.current.muted = v === 0;
    }
  };

  const toggleVolume = () => {
    setShowVolume(!showVolume);
    if (!showVolume) {
      setTimeout(() => { if (!isAdjustingVolume) setShowVolume(false); }, 1800);
    }
  };

  const handleSeek = (e) => {
    const newTime = parseFloat(e.target.value);
    if (audioRef.current && Number.isFinite(newTime)) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  // --- DRAG ---
  const [dragging, setDragging] = useState(false);
  const offset = useRef({ x: 0, y: 0 });
  const playerRef = useRef(null);

  const getClientPos = (e) => {
    if (e.type.startsWith("touch")) {
      return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
    return { x: e.clientX, y: e.clientY };
  };

  const clamp = (val, min, max) => Math.min(Math.max(val, min), max);

  const startDrag = (e) => {
    if (isBlogPostMobile) return;
    if (e.target.closest(".no-drag")) return;
    if (e.type === "mousedown") e.preventDefault();
    setDragging(true);
    const pos = getClientPos(e);
    offset.current = { x: pos.x - position.x, y: pos.y - position.y };
  };

  const onDrag = (e) => {
    if (!dragging) return;
    const pos = getClientPos(e);
    if (e.type.startsWith("touch")) e.preventDefault();

    const newX = pos.x - offset.current.x;
    const newY = pos.y - offset.current.y;

    const playerWidth = playerRef.current?.offsetWidth || 300;
    const playerHeight = playerRef.current?.offsetHeight || 100;

    const maxX = window.innerWidth - playerWidth;
    const maxY = window.innerHeight - playerHeight;
    const minY = Math.max(40, getNavigationOffset());

    setPosition({
      x: clamp(newX, 0, maxX),
      y: clamp(newY, minY, maxY),
    });
  };

  const stopDrag = () => setDragging(false);

  useEffect(() => {
    window.addEventListener("mousemove", onDrag);
    window.addEventListener("mouseup", stopDrag);
    window.addEventListener("touchmove", onDrag, { passive: false });
    window.addEventListener("touchend", stopDrag);
    return () => {
      window.removeEventListener("mousemove", onDrag);
      window.removeEventListener("mouseup", stopDrag);
      window.removeEventListener("touchmove", onDrag);
      window.removeEventListener("touchend", stopDrag);
    };
  }, [dragging]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // --- RENDU ---
  return (
    <>
      <audio
        id="mini-player-audio"
        ref={(el) => {
          audioRef.current = el;
          miniPlayerAudioRef.current = el;
        }}
        preload="metadata"
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
      />

      <motion.div
        ref={playerRef}
        id="draggable-player"
        className="mini-player"
        style={{
          ...(isBlogPostMobile
            ? {
                top: "auto",
                bottom: "14px",
                left: "50%",
                marginLeft: `-${(playerRef.current?.offsetWidth || 320) / 2}px`,
                transform: "none",
                position: "fixed",
              }
            : {
                top: `${position.y}px`,
                bottom: "auto",
                left: `${position.x}px`,
                transform: "none",
                position: "fixed",
              }),
          cursor: isBlogPostMobile ? "default" : dragging ? "grabbing" : "grab",
          zIndex: showMenu ? 998 : 9999,
          background: "rgba(0, 0, 0, 0.9)",
          backdropFilter: "blur(12px)",
          border: "1px solid rgba(100, 200, 255, 0.3)",
          borderRadius: "20px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.8)",
          padding: "8px 6px",
          userSelect: "none",
          transition: dragging ? "none" : "all 0.3s ease",
        }}
        onMouseDown={startDrag}
        onTouchStart={startDrag}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        {/* CONTROLS */}
        <div className="mini-player-controls">
          <button onClick={handlePrevTrack} className="mini-player-btn">
            <SkipBack size={12} />
          </button>

          <button onClick={handlePlayPause} className="mini-player-btn mini-player-main">
            {isPlaying ? <Pause size={14} /> : <Play size={14} />}
          </button>

          <button onClick={handleNextTrack} className="mini-player-btn">
            <SkipForward size={12} />
          </button>

          <div className="mini-player-volume" style={{ position: "relative" }}>
            <button onClick={toggleVolume} className="mini-player-btn">
              {isMuted ? <VolumeX size={12} /> : <Volume2 size={12} />}
            </button>

            {showVolume && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  left: "50%",
                  transform: "translateX(-50%)",
                  background: "rgba(0, 0, 0, 0.95)",
                  border: "1px solid rgba(0, 206, 209, 0)",
                  borderRadius: "12px",
                  padding: "12px",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.8)",
                  zIndex: 10001,
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
              >
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setIsAdjustingVolume(true);
                  }}
                  onMouseUp={() => {
                    setIsAdjustingVolume(false);
                    setShowVolume(false);
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    setIsAdjustingVolume(true);
                  }}
                  onTouchEnd={() => {
                    setIsAdjustingVolume(false);
                    setShowVolume(false);
                  }}
                  className="volume-input"
                  style={{
                    width: "80px",
                    height: "4px",
                    background: `linear-gradient(to right, #00CED1 0%, #00CED1 ${
                      (isMuted ? 0 : volume) * 100
                    }%, #4B5563 ${(isMuted ? 0 : volume) * 100}%, #4B5563 100%)`,
                    borderRadius: "2px",
                    outline: "none",
                    cursor: "pointer",
                  }}
                />
                <div
                  style={{
                    color: "#00CED1",
                    fontSize: "10px",
                    textAlign: "center",
                    marginTop: "6px",
                    fontWeight: "600",
                  }}
                >
                  {Math.round((isMuted ? 0 : volume) * 100)}%
                </div>
              </div>
            )}
          </div>

          {/* PROGRESS */}
          <div
            style={{
              position: "relative",
              flex: 1,
              minWidth: "5px",
              display: "flex",
              alignItems: "center",
              marginTop: "15px",
            }}
          >
            <div
              style={{
                position: "absolute",
                bottom: "calc(50% + 6px)",
                left: 0,
                right: 0,
                overflow: "hidden",
              }}
            >
              <div key={currentTrack} className="track-title">
                {tracks[currentTrack]?.title}
              </div>
            </div>

            <input
              type="range"
              min={0}
              max={Number.isFinite(duration) && duration > 0 ? duration : 0}
              step="0.1"
              value={Number.isFinite(currentTime) ? currentTime : 0}
              onChange={handleSeek}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              className="timeline-range"
              style={{ width: "100%" }}
            />
          </div>

          <div
            style={{
              color: "#9CA3AF",
              fontSize: "12px",
              fontWeight: "300",
              whiteSpace: "nowrap",
              marginLeft: "0px",
              marginTop: "4px",
            }}
          >
            {formatTime(currentTime)}/{formatTime(duration)}
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowPlaylist((prev) => !prev);
            }}
            className="mini-player-btn"
            style={{ marginLeft: "0px", marginTop: "4px" }}
          >
            <ListMusic size={12} />
          </button>
        </div>
      </motion.div>

      {/* PLAYLIST */}
      <AnimatePresence>
        {showPlaylist && (
          <motion.div
            className="mini-player-playlist"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              position: "fixed",
              ...(isBlogPostMobile
                ? {
                    top: "auto",
                    bottom: "88px",
                    left: "50%",
                    marginLeft: `-${(playerRef.current?.offsetWidth || 320) / 2}px`,
                    transform: "none",
                  }
                : {
                    top: `${position.y + 68}px`,
                    bottom: "auto",
                    left: `${position.x}px`,
                    transform: "none",
                  }),
              background: "rgba(0, 0, 0, 0.95)",
              backdropFilter: "blur(12px)",
              border: "1px solid rgba(0, 206, 209, 0.3)",
              borderRadius: "16px",
              padding: "10px",
              maxHeight: "300px",
              overflowY: "auto",
              zIndex: 10000,
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.8)",
            }}
          >
            {tracks.map((track, idx) => (
              <div
                key={track.id}
                onClick={() => {
                  const a = audioRef.current;
                  setCurrentTrack(idx);
                  setShowPlaylist(false);
                  startMiniExclusivity(1800);
                  setActivePlayer("navigation");
                  setTimeout(() => {
                    if (!a) return;
                    const onLoaded = () => {
                      a.removeEventListener("loadeddata", onLoaded);
                      a.play()
                        .then(() => setIsPlaying(true))
                        .catch(() => {
                          const onCanPlay = () => {
                            a.removeEventListener("canplay", onCanPlay);
                            a.play().then(() => setIsPlaying(true)).catch(() => {});
                          };
                          a.addEventListener("canplay", onCanPlay, { once: true });
                          try { a.load(); } catch {}
                        });
                    };
                    a.addEventListener("loadeddata", onLoaded, { once: true });
                    try { a.load(); } catch {}
                  }, 0);
                }}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  fontSize: "12px",
                  color: idx === currentTrack ? "#00CED1" : "#fff",
                  borderRadius: "8px",
                  transition: "all 0.2s",
                  marginBottom: "4px",
                  backgroundColor:
                    idx === currentTrack
                      ? "rgba(0, 206, 209, 0.1)"
                      : "transparent",
                }}
                onMouseEnter={(e) => {
                  if (idx !== currentTrack) {
                    e.currentTarget.style.backgroundColor =
                      "rgba(255, 255, 255, 0.05)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (idx !== currentTrack) {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }
                }}
              >
                <div style={{ fontWeight: "500" }}>{track.title}</div>
                <div
                  style={{
                    fontSize: "10px",
                    color: "#9CA3AF",
                    marginTop: "2px",
                  }}
                >
                  {track.artist}
                </div>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AudioPlayer;